import React, { useState, useEffect } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, Pagination, Table, Button, Rating } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getReviews } from '../../../../services/reviews'
import { getListUsers } from '../../../../services/users'
import { getAllOrderList } from '../../../../services/orders'
import { useNavigate } from 'react-router-dom'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'

const defaultParameter = {
    'page[num]': 1,
    'page[limit]': 10,
    sort: '-id',
}

const ReviewList = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [params, setParams] = useState(defaultParameter)
    const [reviewList, setReviewList] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [userMap, setUserMap] = useState({})
    const [orderMap, setOrderMap] = useState({})

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Settings', route: null },
        { label: 'Review List', route: null },
    ]

    const fetchReviews = async () => {
        setLoading(true)
        try {
            const response = await getReviews(params)
            if (response.success) {
                const data = response.data || []

                const remap = data.map((val) => ({
                    id: val.attributes.id,
                    reviewable_id: val.attributes.reviewable_id,
                    reviewable_type: val.attributes.reviewable_type,
                    point: val.attributes.point,
                    content: val.attributes.content,
                    created_by: val.attributes.created_by,
                    name: val.attributes.name,
                    submitted_at: val.attributes.submitted_at,
                    created_at: val.attributes.created_at,
                }))

                const userIds = [...new Set(remap.map((r) => r.created_by).filter(Boolean))]
                if (userIds.length > 0) {
                    const userRes = await getListUsers({ 'filter[id][in]': userIds.join(',') })
                    const users = {}
                    const userData = userRes?.data?.data || []
                    userData.forEach((u) => {
                        users[u.id] = u.attributes?.name || u.attributes?.username || u.attributes?.email || `User #${u.id}`
                    })
                    setUserMap((prev) => ({ ...prev, ...users }))
                }

                const orderIds = [...new Set(remap.filter((r) => r.reviewable_type?.includes('order')).map((r) => r.reviewable_id).filter(Boolean))]
                if (orderIds.length > 0) {
                    const orderRes = await getAllOrderList({ 'filter[id][in]': orderIds.join(','), 'fields[order]': 'id,order_code' })
                    const orders = {}
                    const orderData = orderRes?.data || []
                    orderData.forEach((o) => {
                        orders[o.id] = o.attributes?.order_code || `Order #${o.id}`
                    })
                    setOrderMap((prev) => ({ ...prev, ...orders }))
                }

                setReviewList(remap)
                setTotalData(response.meta?.pagination?.total || 0)
            } else {
                setReviewList([])
                setTotalData(0)
            }
        } catch (error) {
            console.log(error)
            setReviewList([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        dispatch(updateRoute({ data: mappingRoute }))
        // eslint-disable-next-line
    }, [dispatch])

    useEffect(() => {
        fetchReviews()
        // eslint-disable-next-line
    }, [params])

    const handleChangePage = (val) => {
        setParams((oldVal) => ({ ...oldVal, 'page[num]': val }))
    }

    return (
        <AuthLayout>
            <Box my={20}>
                <Flex justify='space-between' align="center" mb={30}>
                    <Text className={classes.titleLayout}>Review List</Text>
                </Flex>
                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data Found: </Text>
                        <Text fz={12} fw='bold'>{totalData} Reviews</Text>
                    </Group>
                    {loading ? (
                        <LoadingData />
                    ) : reviewList.length > 0 ? (
                        <Table.ScrollContainer minWidth={800} type='native'>
                            <Table highlightOnHover withTableBorder style={{ fontSize: '13px' }}>
                                <Table.Thead style={{ backgroundColor: '#eeefef' }}>
                                    <Table.Tr>
                                        <Table.Th width={50}>ID</Table.Th>
                                        <Table.Th width={150}>Reviewable</Table.Th>
                                        <Table.Th width={80}>Point</Table.Th>
                                        <Table.Th width={300}>Content</Table.Th>
                                        <Table.Th width={150}>Created By</Table.Th>
                                        <Table.Th width={150}>Submitted At</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody style={{ verticalAlign: 'top' }}>
                                    {reviewList.map((val) => (
                                        <Table.Tr key={val.id}>
                                            <Table.Td>{val.id}</Table.Td>
                                            <Table.Td>
                                                {val.reviewable_type?.includes('order') ? (
                                                    <Button
                                                        variant="subtle"
                                                        size="compact-xs"
                                                        fz={12}
                                                        onClick={() => navigate(`/order/${val.reviewable_id}`)}
                                                    >
                                                        {orderMap[val.reviewable_id] || `Order #${val.reviewable_id}`}
                                                    </Button>
                                                ) : (
                                                    <Text fz={12}>{val.reviewable_type} #{val.reviewable_id}</Text>
                                                )}
                                            </Table.Td>
                                            <Table.Td>
                                                <Rating value={val.point} readOnly size="xs" />
                                            </Table.Td>
                                            <Table.Td>{val.content || '-'}</Table.Td>
                                            <Table.Td>
                                                <Button
                                                    variant="subtle"
                                                    size="compact-xs"
                                                    fz={12}
                                                    onClick={() => navigate(`/users/${val.created_by}`)}
                                                >
                                                    {userMap[val.created_by] || `User #${val.created_by}`}
                                                </Button>
                                            </Table.Td>
                                            <Table.Td>{val.submitted_at || '-'}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    ) : (
                        <NoData />
                    )}
                </Box>
                <Flex justify='end'>
                    <Pagination
                        size='sm'
                        onChange={handleChangePage}
                        value={params['page[num]']}
                        total={totalData > 0 ? Math.ceil(totalData / params['page[limit]']) : 0}
                    />
                </Flex>
            </Box>
        </AuthLayout>
    )
}

export default ReviewList
